import { supabase } from '../lib/supabaseClient';
import type { Room } from '../types';
import { calculateArea, getRoomBounds } from './floorPlanUtils';

// Unified data structure for the entire retrofit assessment survey
export interface RetrofitSurveyData {
  // Meta information
  id?: string;
  created_at?: string;
  updated_at?: string;
  name: string;
  version: string;
  additionalNotes?: string;
  
  // Basic Information (Initial Questionnaire)
  initial_questionnaire: {
    surveyorName: string;
    surveyDate: string;
    customerFullName: string;
    customerEmail: string;
    customerMobile: string;
    buildingName?: string;
    houseNumberName?: string;
    streetAddress: string;
    town: string;
    postCode: string;
    habitableRooms: string;
    heatedHabitableRooms: string;
    totalRooms: string;
    tenure: string;
    buildingAge: string;
    dwellingType: string;
    buildingOrientation: string;
    majorityWallType: string;
    // Flat-specific fields
    flatFloorNumber?: string; // What floor(s) is the flat on
    corridorHeating?: 'heated' | 'unheated' | 'none'; // Corridor heating status
    hasMainsGas: boolean | null;
    hasOilLpgTank: boolean | null;
    hasRenewables: boolean | null;
    hasGasMeter?: boolean | null;
    gasMeterLocation?: string;
    hasElectricMeter?: boolean | null;
    electricMeterLocation?: string;
    hasConsumerBoard?: boolean | null;
    consumerBoardLocation?: string;
    hasStopTap?: boolean | null;
    stopTapLocation?: string;
    additionalNotes: string;
    photos: Record<string, {
      photos: Array<{
        id: string;
        fileName?: string;
        url?: string; // Supabase URL
        localPath?: string; // Temporary base64 during upload
        uploading?: boolean;
        error?: string;
        timestamp?: string;
        note?: string;
        caption?: string;
        order?: number;
      }>;
    }>;
  };
  
  // Floor Plan Data (references existing floor plan)
  floor_plan: {
    floorPlanId?: string;
    linkedFloorPlanData?: any; // Can store floor plan data or just reference
    hlpLength?: number; // Heat Loss Perimeter in meters (auto-calculated)
    pwLength?: number; // Party Wall length in meters (auto-calculated)
    totalArea?: number; // Total floor area in m² (auto-calculated)
  };
  
  // Occupancy Assessment
  occupancy_assessment: {
    numberOfOccupants: string;
    ageGroups: string[];
    occupancyPattern: string;
    heatingPattern: string;
    ventilationHabits: string;
    notes: string;
    // Additional fields from OccupancyAssessmentForm
    numberOfChildren?: string;
    numberOfDisabled?: string;
    numberOfElderly?: string;
    singleBedSpaces?: string;
    doubleBedSpaces?: string;
    isHeatingTypical?: boolean | null;
    // Customer documents
    hasCustomerDocuments?: boolean | null;
    customerDocumentsReason?: string; // Reason if no documents
    customerDocumentsPhotos?: any[]; // Photos if has documents
  };
  
  // Ventilation & Condition Survey
  ventilation_condition: {
    // Room Counts
    habitableRooms?: string;
    heatedHabitableRooms?: string;
    totalRooms?: string;
    // Windows
    windowFramesCondition?: string;
    draughtProofingCondition?: string;
    windowFrameType?: string;
    numberOfWindows?: string;
    numberOfTrickleVents?: string;
    windowGlazingType?: string;
    windowGlazingMeasurement?: string;
    windowGlazingThicknessImage?: Array<{
      displayId: string;
      fileName?: string;
      url?: string;
      localPath?: string;
      dataUrl?: string;
      uploading?: boolean;
      error?: string;
    }>;
    // Doors
    numberOfExternalDoors?: string;
    externalDoorsCondition?: string;
    externalDoorsType?: string;
    draughtProofingOnExternalDoor?: string;
    doorUndercutsPresent?: boolean | null; // Legacy field for backward compatibility
    doorPresent?: boolean | null; // New field
    doorUndercutSize?: string; // New field
    roomsWithNoDoorUndercuts?: string;
    externalDoorsImages?: Array<{
      displayId: string;
      fileName?: string;
      url?: string;
      localPath?: string;
      dataUrl?: string;
      uploading?: boolean;
      error?: string;
    }>;
    frontDoorOpenImage?: Array<{
      displayId: string;
      fileName?: string;
      url?: string;
      localPath?: string;
      dataUrl?: string;
      uploading?: boolean;
      error?: string;
    }>;
    // Ventilation Fans
    numberOfWetRooms?: string;
    numberOfVentilationFans?: string;
    roomsMissingVentilationFans?: string;
    ventilationTypePresent?: string[];
    ventilationTypeCounts?: {
      decentralised_mechanical: number;
      intermittent: number;
      centralised_mechanical: number;
      other: number;
    };
    // Roof
    roofType?: string;
    externalRoofCondition?: string;
    visibleExternalRoofDefects?: boolean | null;
    roofDefectImages?: Array<{
      displayId: string;
      fileName?: string;
      url?: string;
      localPath?: string;
      dataUrl?: string;
      uploading?: boolean;
      error?: string;
    }>;
    // Internal Walls
    internalWallCondition?: string;
    evidenceOfDamp?: boolean | null;
    dampnessImages?: Array<{
      displayId: string;
      fileName?: string;
      url?: string;
      localPath?: string;
      dataUrl?: string;
      uploading?: boolean;
      error?: string;
    }>;
    // Floor
    floorConstructionType?: string;
    adequateSubFloorVentilation?: boolean | null;
    evidenceOfFloorDampness?: boolean | null;
    // External Walls
    externalWallType?: string;
    externalWallCondition?: string;
    additionalVisibleWallDefects?: boolean | null;
    wallDefectImages?: Array<{
      displayId: string;
      fileName?: string;
      url?: string;
      localPath?: string;
      dataUrl?: string;
      uploading?: boolean;
      error?: string;
    }>;
    // Legacy fields (kept for backward compatibility)
    naturalVentilation?: string;
    mechanicalVentilation?: string;
    airTightnessAssessment?: string;
    moistureIssues?: string;
    condensationProblems?: string;
    ventilationEffectiveness?: string;
    notes?: string;
  };
  
  // Heating System
  heating_system: {
    // PCDB Boiler Details
    boilerManufacturer?: string;
    boilerBrand?: string;
    modelQualifier?: string;
    boilerType?: string;
    fuelType?: string;
    sapWinterEfficiency?: string;
    boilerAge?: string;
    commissionYear?: string;
    serialNumber?: string;
    boilerLocation?: string;
    boilerPosition?: string;
    boilerPositionOther?: string;
    mainHeatingSource?: string;
    heatingSystemCondition?: string;
    // Boolean fields
    hasCondensatePipe?: boolean | null;
    hasOilLpgTank?: boolean | null;
    hasRenewables?: boolean | null;
    hasCylinderWaterHeating?: boolean | null;
    hasOtherBoilers?: boolean | null;
    otherBoilersDetails?: string;
    // Heating Controls fields
    fullSetFunctioningControls?: boolean | null;
    systemHasBypassRadiator?: boolean | null;
    // Legacy fields (backward compatibility)
    primaryHeatingType?: string;
    heatingFuel?: string;
    boilerCondition?: string;
    heatingControls?: string;
    radiatorTypes?: string[];
    distributionSystem?: string;
    efficiency?: string;
    notes?: string;
    selectedPCDBSystem?: any; // Store selected PCDB heating system for persistence
    photos?: any; // Flexible photo storage for different structures
  };
  
  // Site Assessment
  site_assessment: {
    // Working at Heights / Access
    conservatoryLadderRequired?: boolean | null;
    loftWalkBoardsRequired?: boolean | null;
    loftGuardrailsRequired?: boolean | null;
    loftLightingRequired?: boolean | null;
    specialistScaffoldingRequired?: boolean | null;
    ladderRequired?: boolean | null;
    ladderMatsRequired?: boolean | null;
    catLadderRequired?: boolean | null;
    tetraAndRunningLineRequired?: boolean | null;
    tetraHarnessRequired?: boolean | null;
    flatRoofLanyardRequired?: boolean | null;
    flatRoofProtectionRequired?: boolean | null;
    // Site & Drilling Concerns
    drillingConcerns?: boolean | null;
    aggressivePeoplePresent?: boolean | null;
    rcd?: boolean | null;
    loftBoarding?: boolean | null;
    // Asbestos
    existingAsbestosReports?: boolean | null;
    knownAsbestos?: boolean | null;
    asbestosIndicated?: boolean | null;
    asbestosSuspected?: boolean | null;
    asbestosPresent?: boolean | null;
    // Risk Assessment
    petsIdentified?: boolean | null;
    environmentalImpactAssessed?: boolean | null;
    batsNestingBirdsPresent?: boolean | null;
    fishProtected?: boolean | null;
    hazardsForIndividuals?: boolean | null;
    knotweedPresent?: boolean | null;
    protectedSpeciesInLoft?: boolean | null;
    finlockGutters?: boolean | null;
    accessAroundProperty?: boolean | null;
    verminEvidence?: boolean | null;
    protectedWildlifeEvidence?: boolean | null;
    scaffoldingErected?: boolean | null;
    additionalLightingRequired?: boolean | null;
    electricalPowerAvailable?: boolean | null;
    gasSupplyRisk?: boolean | null;
    animalSignsOnApproach?: boolean | null;
    foulWaterPointsAvailable?: boolean | null;
    pathsDrivewaysGoodOrder?: boolean | null;
    goodWorkingEnvironment?: boolean | null;
    overheadCablesPresent?: boolean | null;
    hasGarage?: boolean | null;
    hasPorch?: boolean | null;
    hasOutsideTap?: boolean | null;
    // Health and Safety
    worksCompleteStandardsInformed?: boolean | null;
    glovesWipedDown?: boolean | null;
    wasteDisposedCorrectly?: boolean | null;
    handsWashed?: boolean | null;
    dailyWasteDisposed?: boolean | null;
    // Legacy fields (backward compatibility)
    accessConstraints?: string[];
    constructionConstraints?: string[];
    planningConsiderations?: string[];
    neighbourhoodContext?: string;
    externalSpaceAvailable?: string;
    notes?: string;
  };
  
  // Measure Specification
  measure_specification: {
    suitableMeasures?: string[]; // Selected measures from the form (Wall Insulation, Loft insulation, RIR insulation, Heating System, No Measures)
    recommendedMeasures: string[];
    priorityOrder: string[];
    estimatedCosts: Record<string, string>;
    implementationPhases: string[];
    notes: string;
    
    // Wall Insulation fields (for when 'Wall insulation' is in recommendedMeasures)
    wallInsulationType?: string;
    wallInsulationElevationsCount?: string;
    wallInsulationElevations?: {
      id: number; // Changed from string to number to support multiple elevations of same type
      type: string;
      dimensions?: {
        height: number;
        width: number;
      };
      elements?: {
        doors: Array<{
          id: string;
          height?: number;
          width?: number;
        }>;
        windows: Array<{
          id: string;
          height?: number;
          width?: number;
        }>;
        airVents: Array<{
          id: string;
        }>;
      };
      calculations?: {
        totalWallArea: number;
        totalElementsArea: number;
        treatableArea: number;
      };
      placedElements?: Array<{
        id: string;
        type: string;
        x: number;
        y: number;
        elevation: string;
        height?: number;
        width?: number;
      }>;
      wallInsulationType?: string;
    }[];
    wallInsulationGlobalCalculations?: {
      totalDwellingArea: number;
      totalUntreatableArea: number;
      totalTreatableArea: number;
      totalWallAreaWholeHouse: number;
      poptPercentage: number;
    };
    
    // Room in Roof (RIR) Insulation fields (for when 'RIR insulation' is in recommendedMeasures)
    roomInRoofData?: {
      measurements: Array<{
        id: string;
        label: string;
        value: string;
        unit: string;
        length?: string;
        height?: string;
        insulation?: string;
        insulationType?: string;
        visible?: boolean;
      }>;
      annotations: Array<{
        id: string;
        x: number;
        y: number;
        text: string;
      }>;
      insulationType?: string;
      ventilationType?: string;
      diagramType?: 'standard' | 'with-dormer';
      diagramImage?: string;
    };
    
    // RIR Photo Evidence fields
    rirExteriorElevations?: any[];
    rirInternalRoomPhotos?: any[];
    rirFlatCeilingInsulation?: any[];
    rirSlopingCeilingInsulation?: any[];
    rirStudWallsInsulation?: any[];
    rirDormerPhotos?: any[];
    dormerPresent?: boolean | null;
    
    // Loft Survey String/Number fields
    mainPropertyRoofType?: string;
    loftNeedsInsulating?: boolean | null;
    loftHasExistingInsulation?: boolean | null;
    mainLoftDepthOfInsulation?: string;
    mainPropertyLoftLength?: string;
    mainPropertyLoftWidth?: string;
    mainPropertyLoftHeight?: string;
    mainPropertyRoofPitch?: string;
    
    // Loft Survey Boolean fields
    walkboardsRequired?: boolean | null;
    tenantsBelongingsToBeRemoved?: boolean | null;
    explainedToOccupier?: boolean | null;
    loftDownlightsPresent?: boolean | null;
    downlightCoversRequired?: boolean | null;
    coldWaterTankPresent?: boolean | null;
    pipeworkPresent?: boolean | null;
    coldWaterTankPipeworkNeedInsulation?: boolean | null;
    roofVentsPresent?: boolean | null;
    verticalFlueInLoft?: boolean | null;
    highCurrentCablesPresent?: boolean | null;
    
    // Loft Survey Photo fields
    mainPropertyInsulationDepthEvidence?: any[];
    mainLoftHatchEvidence?: any[];
    mainPropertyRoofPitchEvidence?: any[];
    mainLoftAreaImages?: any[];
    apexMeasurementEvidence?: any[];
    partyWallEvidence?: any[];
    downlightImage?: any[];
    coldWaterTankPipeworkEvidence?: any[];
    roofVentsImage?: any[];
    
    // PAS 2030 Survey Questions (dynamically generated based on selected measures)
    pas2030Questions?: {
      heatingSystem?: Record<string, boolean | string>; // Boiler/Heating System questions
      iwi?: Record<string, boolean | string>; // Internal Wall Insulation questions
      ewi?: Record<string, boolean | string>; // External Wall Insulation questions
      cwi?: Record<string, boolean | string>; // Cavity Wall Insulation questions
      wallInsulation?: Record<string, boolean | string>; // Generic wall insulation (backwards compatibility)
      rirInsulation?: Record<string, boolean | string>; // RIR questions
    };
  };
  
  // Property Elevations
  property_front: {
    buildForm: string;
    buildingOrientation?: string; // Building orientation in degrees
    exposedSide: 'left' | 'right' | 'both' | 'none' | '';
    wallConstruction: string;
    windowTypes: string[];
    doorTypes: string[];
    roofType: string;
    roofCondition?: string; // good, average, or bad
    hasExternalRoofDefects?: boolean | null; // External roof defects yes/no
    externalRoofDefectsImages?: any[]; // Images of external roof defects
    gutteringCondition?: string; // good, average, or poor
    notes: string;
    photos: string[];
    // Additional fields from updated PropertyFrontForm
    externalWallType?: string;
    externalWallCondition?: string;
    hasWallDefects?: boolean | null;
    propertyWallConstructionType?: string;
    propertyMainConstructionWallType?: string;
    isWallInsulated?: boolean | null;
    wallThickness?: string;
    // Floor construction fields
    floorConstructionType?: string;
    adequateSubFloorVentilation?: boolean | null;
    evidenceOfFloorDampness?: boolean | null;
    dpcPresent?: boolean | null;
    dpcVisible?: boolean | null;
    additionalNotes?: string;
    // Photo fields
    detachmentType?: any;
    orientationImage?: any[]; // Building orientation photos
    frontElevationImage?: any;
    roofTypeEvidenceImages?: any[];
    gutteringDrainageImages?: any[];
    wallTypeEvidenceImage?: any;
    wallThicknessImages?: any[]; // Deprecated - kept for backward compatibility
    wallThicknessFromDoor?: any[]; // New - wall thickness from door
    wallThicknessFromWindow?: any[]; // New - wall thickness from window
    dpcEvidenceImages?: any[];
    wallDefectImages?: any[];
    floorConstructionEvidenceImages?: any[];
  };
  
  property_rear: {
    wallConstruction: string;
    windowTypes: string[];
    doorTypes: string[];
    extensions: string[];
    notes: string;
    photos: string[];
    // Additional fields from PropertyRearForm
    externalWallType?: string;
    externalWallCondition?: string;
    hasWallDefects?: boolean | null;
    isWallInsulated?: boolean | null;
    hasBackyard?: boolean | null;
    additionalNotes?: string;
    // Photo fields
    rearElevationImage?: any;
    wallDefectImages?: any[];
    roofTypeEvidenceImages?: any[];
    rearBackyardImages?: any[];
    gutteringDrainageImages?: any[];
    wallTypeEvidenceImage?: any;
  };
  
  property_right: {
    wallConstruction: string;
    windowTypes: string[];
    exposureLevel: string;
    notes: string;
    photos: string[];
    // Additional fields from PropertyRightForm
    externalWallType?: string;
    externalWallCondition?: string;
    hasWallDefects?: boolean | null;
    propertyWallConstructionType?: string;
    isWallInsulated?: boolean | null;
    additionalNotes?: string;
    // Photo fields
    rightElevationImage?: any;
    wallDefectImages?: any[];
    roofTypeEvidenceImages?: any[];
    rightSideImages?: any[];
    gutteringDrainageImages?: any[];
    wallTypeEvidenceImage?: any;
  };
  
  property_left: {
    wallConstruction: string;
    windowTypes: string[];
    exposureLevel: string;
    notes: string;
    photos: string[];
    // Additional fields from PropertyLeftForm
    externalWallType?: string;
    externalWallCondition?: string;
    hasWallDefects?: boolean | null;
    propertyWallConstructionType?: string;
    isWallInsulated?: boolean | null;
    additionalNotes?: string;
    // Photo fields
    leftElevationImage?: any;
    wallDefectImages?: any[];
    roofTypeEvidenceImages?: any[];
    leftSideImages?: any[];
    gutteringDrainageImages?: any[];
    wallTypeEvidenceImage?: any;
  };
  
  // Room Details
  room_details: {
    rooms: Array<{
      id: string;
      name: string;
      abbreviation?: string;
      floor: number;
      usage: string;
      heatingType: string;
      ventilationType: string;
      condition: string;
      notes: string;
      photos: string[];
      // Dimensions from floor plan
      width?: number; // in meters
      length?: number; // in meters
      height?: number; // in meters
      area?: number; // in square meters
      // Room classification
      roomType?: string; // Wet Room | Habitable | N/A
      heatingStatus?: string; // Heated | Unheated (for habitable rooms)
      // Light counts
      ledLightsCount?: number;
      nonLedLightsCount?: number;
      // Alarm counts
      smokeAlarmsCount?: number;
      carbonMonoxideAlarmsCount?: number;
      heatAlarmsCount?: number;
    }>;
  };
  
  // Main Property Measurements
  main_property_measurements: {
    floorArea: Record<number, string>; // floor number -> area
    wallAreas: Record<string, string>; // wall type -> area
    windowAreas: Record<string, string>; // window type -> area
    doorAreas: Record<string, string>; // door type -> area
    roofArea: string;
    notes: string;
    // Additional fields from MainPropertyMeasurementsForm
    numberOfStoreys?: string;
    hasCellar?: boolean | null;
    totalArea?: string;
    storeyMeasurements?: Array<{
      id: string;
      name: string;
      height: string;
    }>;
  };
  
  // Extensions
  extensions: {
    existingExtensions: Array<{
      id: string;
      type: string;
      age: string;
      condition: string;
      heatingProvision: string;
      notes: string;
      area?: string; // Area in m² from floor plan calculations
      numberOfStoreys?: string;
      buildingAge?: string;
      wallConstructionType?: string;
      isWallInsulated?: boolean | null;
      roofType?: string;
      photos?: any; // Extension photos
    }>;
    proposedExtensions: Array<{
      id: string;
      type: string;
      purpose: string;
      estimatedArea: string;
      notes: string;
    }>;
    // Additional fields from ExtensionsForm
    numberOfExtensions?: string;
    hasConservatory?: boolean | null;
    conservatoryPhotos?: {
      externalElevationImages: any[];
      internalImages: any[];
    };
  };
  
  // Signatures & Feedback
  signatures?: {
    // Pre-signature assessment questions
    asbestosChecksUndertaken?: boolean | null;
    servicesAffectedByInstallation?: boolean | null;
    potentialRiskToServices?: boolean | null;
    installationInLineWithDesign?: boolean | null;
    methodStatementSuitable?: boolean | null;
    protectedSpeciesDisturbance?: boolean | null;
    customerUnderstandsWork?: boolean | null;
    customerAwareOfDisruption?: boolean | null;
    customerHasRequests?: boolean | null;
    siteAccessArrangementsAdequate?: boolean | null;
    otherInstallationInfo?: boolean | null;
    potentialMoistureBuildUp?: boolean | null;
    
    customer?: {
      statement1?: string;
      statement2?: string;
      statement3?: string;
      statement4?: string;
      statement1Date?: string;
      statement2Date?: string;
      statement3Date?: string;
      statement4Date?: string;
    };
    surveyor?: {
      statement1?: string;
      statement2?: string;
      statement3?: string;
      statement4?: string;
      statement5?: string;
      statement1Date?: string;
      statement2Date?: string;
      statement3Date?: string;
      statement4Date?: string;
      statement5Date?: string;
    };
    feedback?: {
      rating?: number | null;
      comments?: string;
    };
  };
  
  // Custom fields from form builder (dynamic fields)
  custom_fields?: Record<string, any>;
}

// Create default retrofit survey data
export const createDefaultRetrofitSurveyData = (): RetrofitSurveyData => ({
  name: '',
  version: '1.0.0',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  initial_questionnaire: {
    surveyorName: '',
    surveyDate: '',
    customerFullName: '',
    customerEmail: '',
    customerMobile: '',
    houseNumberName: '',
    streetAddress: '',
    town: '',
    postCode: '',
    habitableRooms: '',
    heatedHabitableRooms: '',
    totalRooms: '',
    tenure: '',
    buildingAge: '',
    dwellingType: '',
    buildingOrientation: '',
    majorityWallType: '',
    hasMainsGas: null,
    hasOilLpgTank: null,
    hasRenewables: null,
    additionalNotes: '',
    photos: {}
  },
  floor_plan: {
    floorPlanId: undefined,
    linkedFloorPlanData: undefined
  },
  occupancy_assessment: {
    numberOfOccupants: '',
    ageGroups: [],
    occupancyPattern: '',
    heatingPattern: '',
    ventilationHabits: '',
    notes: ''
  },
  ventilation_condition: {
    naturalVentilation: '',
    mechanicalVentilation: '',
    airTightnessAssessment: '',
    moistureIssues: '',
    condensationProblems: '',
    ventilationEffectiveness: '',
    notes: ''
  },
  heating_system: {
    primaryHeatingType: '',
    heatingFuel: '',
    boilerAge: '',
    boilerCondition: '',
    heatingControls: '',
    radiatorTypes: [],
    distributionSystem: '',
    efficiency: '',
    notes: ''
  },
  site_assessment: {
    accessConstraints: [],
    constructionConstraints: [],
    planningConsiderations: [],
    neighbourhoodContext: '',
    externalSpaceAvailable: '',
    notes: ''
  },
  measure_specification: {
    suitableMeasures: [],
    recommendedMeasures: [],
    priorityOrder: [],
    estimatedCosts: {},
    implementationPhases: [],
    notes: '',
    rirExteriorElevations: [],
    rirInternalRoomPhotos: [],
    rirFlatCeilingInsulation: [],
    rirSlopingCeilingInsulation: [],
    rirStudWallsInsulation: [],
    rirDormerPhotos: [],
    dormerPresent: null,
    walkboardsRequired: null,
    tenantsBelongingsToBeRemoved: null,
    explainedToOccupier: null,
    loftDownlightsPresent: null,
    downlightCoversRequired: null,
    coldWaterTankPresent: null,
    pipeworkPresent: null,
    coldWaterTankPipeworkNeedInsulation: null,
    roofVentsPresent: null,
    verticalFlueInLoft: null,
    highCurrentCablesPresent: null,
    mainPropertyInsulationDepthEvidence: [],
    mainLoftHatchEvidence: [],
    mainPropertyRoofPitchEvidence: [],
    mainLoftAreaImages: [],
    apexMeasurementEvidence: [],
    partyWallEvidence: [],
    downlightImage: [],
    coldWaterTankPipeworkEvidence: [],
    roofVentsImage: []
  },
  property_front: {
    buildForm: '',
    exposedSide: '',
    wallConstruction: '',
    windowTypes: [],
    doorTypes: [],
    roofType: '',
    notes: '',
    photos: []
  },
  property_rear: {
    wallConstruction: '',
    windowTypes: [],
    doorTypes: [],
    extensions: [],
    notes: '',
    photos: []
  },
  property_right: {
    wallConstruction: '',
    windowTypes: [],
    exposureLevel: '',
    notes: '',
    photos: []
  },
  property_left: {
    wallConstruction: '',
    windowTypes: [],
    exposureLevel: '',
    notes: '',
    photos: []
  },
  room_details: {
    rooms: []
  },
  main_property_measurements: {
    floorArea: {},
    wallAreas: {},
    windowAreas: {},
    doorAreas: {},
    roofArea: '',
    notes: ''
  },
  extensions: {
    existingExtensions: [],
    proposedExtensions: []
  }
});

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// Helper function to calculate total storage size for all photos in a survey
const calculateStorageSize = async (data: RetrofitSurveyData): Promise<{ totalBytes: number; photoCount: number; breakdown: any }> => {
  const photoUrls: string[] = [];
  
  // Recursively extract all photo URLs from the survey data
  const extractUrls = (value: any) => {
    if (Array.isArray(value)) {
      value.forEach(item => extractUrls(item));
    } else if (value && typeof value === 'object') {
      // Check if this object is a photo
      if (value.url && typeof value.url === 'string' && value.url.includes('supabase.co/storage')) {
        photoUrls.push(value.url);
      } else {
        // Recursively check nested objects
        Object.values(value).forEach(nestedValue => extractUrls(nestedValue));
      }
    }
  };
  
  extractUrls(data);
  
  // Remove duplicates
  const uniqueUrls = [...new Set(photoUrls)];
  
  // Get size for each photo using HEAD requests
  let totalBytes = 0;
  let successCount = 0;
  let errorCount = 0;
  
  // Process in batches to avoid overwhelming the browser
  const batchSize = 10;
  for (let i = 0; i < uniqueUrls.length; i += batchSize) {
    const batch = uniqueUrls.slice(i, i + batchSize);
    
    await Promise.all(
      batch.map(async (url) => {
        try {
          // Make HEAD request to get Content-Length without downloading the file
          const response = await fetch(url, { method: 'HEAD' });
          
          if (response.ok) {
            const contentLength = response.headers.get('Content-Length');
            if (contentLength) {
              const size = parseInt(contentLength, 10);
              totalBytes += size;
              successCount++;
            } else {
              errorCount++;
            }
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      })
    );
  }
  
  return {
    totalBytes,
    photoCount: uniqueUrls.length,
    breakdown: {
      successCount,
      errorCount,
      uniqueUrls: uniqueUrls.length
    }
  };
};

// Helper function to analyze size breakdown of retrofit survey
const analyzeSurveySize = (data: RetrofitSurveyData): { 
  totalSize: number;
  photoRelatedSize: number;
  otherDataSize: number;
  breakdown: {
    initial_questionnaire_photos: number;
    heating_system_photos: number;
    property_photos: number;
    room_photos: number;
    other_data: number;
  }
} => {
  const dataClone = JSON.parse(JSON.stringify(data));
  const totalSize = new Blob([JSON.stringify(data)]).size;
  
  // Calculate photo-related sizes by temporarily removing and measuring
  let photoRelatedSize = 0;
  const breakdown = {
    initial_questionnaire_photos: 0,
    heating_system_photos: 0,
    property_photos: 0,
    room_photos: 0,
    other_data: 0
  };
  
  // Measure initial_questionnaire photos
  if (dataClone.initial_questionnaire?.photos) {
    const withPhotos = new Blob([JSON.stringify(dataClone.initial_questionnaire.photos)]).size;
    breakdown.initial_questionnaire_photos = withPhotos;
    photoRelatedSize += withPhotos;
  }
  
  // Measure heating_system photos
  if (dataClone.heating_system?.photos) {
    const withPhotos = new Blob([JSON.stringify(dataClone.heating_system.photos)]).size;
    breakdown.heating_system_photos = withPhotos;
    photoRelatedSize += withPhotos;
  }
  
  // Measure property photos (front, rear, left, right)
  ['property_front', 'property_rear', 'property_left', 'property_right'].forEach(section => {
    const sectionData = (dataClone as any)[section];
    if (sectionData) {
      // Find all photo-related fields
      const photoFields = Object.keys(sectionData).filter(key => 
        key.toLowerCase().includes('image') || 
        key.toLowerCase().includes('photo') ||
        Array.isArray(sectionData[key])
      );
      photoFields.forEach(field => {
        const fieldSize = new Blob([JSON.stringify(sectionData[field])]).size;
        breakdown.property_photos += fieldSize;
        photoRelatedSize += fieldSize;
      });
    }
  });
  
  // Measure room photos
  if (dataClone.room_details?.rooms && Array.isArray(dataClone.room_details.rooms)) {
    dataClone.room_details.rooms.forEach((room: any) => {
      // Find photo-related fields in room
      const photoFields = Object.keys(room).filter(key => 
        key.toLowerCase().includes('image') || 
        key.toLowerCase().includes('photo') ||
        (Array.isArray(room[key]) && room[key].length > 0 && typeof room[key][0] === 'object' && 
         (room[key][0].url || room[key][0].fileName))
      );
      photoFields.forEach(field => {
        const fieldSize = new Blob([JSON.stringify(room[field])]).size;
        breakdown.room_photos += fieldSize;
        photoRelatedSize += fieldSize;
      });
    });
  }
  
  // Calculate other data size
  const otherDataSize = totalSize - photoRelatedSize;
  breakdown.other_data = otherDataSize;
  
  return {
    totalSize,
    photoRelatedSize,
    otherDataSize,
    breakdown
  };
};

// Helper function to analyze photos in retrofit survey data
const analyzePhotosInRetrofitSurvey = (data: RetrofitSurveyData): { count: number; base64Count: number; totalBase64Size: number } => {
  let count = 0;
  let base64Count = 0;
  let totalBase64Size = 0;
  
  const checkPhoto = (photo: any) => {
    count++;
    // Check multiple possible fields for base64 data
    if (photo?.url && photo.url.startsWith('data:image')) {
      base64Count++;
      const base64Data = photo.url.split(',')[1] || '';
      totalBase64Size += base64Data.length;
    }
    if (photo?.localPath && photo.localPath.startsWith('data:image')) {
      const base64Data = photo.localPath.split(',')[1] || '';
      totalBase64Size += base64Data.length;
    }
    if (photo?.dataUrl && photo.dataUrl.startsWith('data:image')) {
      const base64Data = photo.dataUrl.split(',')[1] || '';
      totalBase64Size += base64Data.length;
    }
    if (photo?.base64String) {
      totalBase64Size += photo.base64String.length;
    }
  };
  
  // Initial questionnaire photos
  if (data.initial_questionnaire?.photos) {
    Object.entries(data.initial_questionnaire.photos).forEach(([sectionName, section]: [string, any]) => {
      section.photos?.forEach((photo: any) => {
        // Debug: log first photo structure from each section to see what fields it has
        if (section.photos.length > 0 && photo === section.photos[0]) {
          console.log(`[Photo Analysis] ${sectionName} photo structure:`, {
            keys: Object.keys(photo),
            hasUrl: !!photo.url,
            hasLocalPath: !!photo.localPath,
            hasDataUrl: !!photo.dataUrl,
            hasBase64String: !!photo.base64String,
            hasFileName: !!photo.fileName
          });
        }
        checkPhoto(photo);
      });
    });
  }
  
  // Heating system photos - heating_system.photos is { [photoType]: DisplayablePhotoData[] }
  if (data.heating_system?.photos) {
    Object.entries(data.heating_system.photos).forEach(([photoType, photoArray]: [string, any]) => {
      if (Array.isArray(photoArray)) {
        photoArray.forEach((photo: any) => {
          if (photo && typeof photo === 'object') {
            checkPhoto(photo);
          }
        });
      }
    });
  }
  
  // Property elevation photos
  const checkElevationPhotos = (elevation: any) => {
    if (elevation) {
      // Array photo fields
      ['orientationImage', 'roofTypeEvidenceImages', 'gutteringDrainageImages', 'wallThicknessImages', 'wallThicknessFromDoor', 'wallThicknessFromWindow', 'wallDefectImages', 'rearBackyardImages', 'rightSideImages', 'leftSideImages'].forEach(field => {
        elevation[field]?.forEach?.(checkPhoto);
      });
      // Single photo fields
      ['frontElevationImage', 'wallTypeEvidenceImage', 'rearElevationImage', 'rightElevationImage', 'leftElevationImage'].forEach(field => {
        if (elevation[field]) checkPhoto(elevation[field]);
      });
    }
  };
  
  checkElevationPhotos(data.property_front);
  checkElevationPhotos(data.property_rear);
  checkElevationPhotos(data.property_left);
  checkElevationPhotos(data.property_right);
  
  // Room photos (note: room.photos is string[], not photo objects)
  if (data.room_details?.rooms) {
    data.room_details.rooms.forEach(room => {
      if (Array.isArray(room.photos)) {
        room.photos.forEach(photoUrl => {
          if (photoUrl) {
            count++;
            // Check if it's a base64 URL
            if (typeof photoUrl === 'string' && photoUrl.startsWith('data:image')) {
              base64Count++;
              const base64Data = photoUrl.split(',')[1] || '';
              totalBase64Size += base64Data.length;
            }
          }
        });
      }
    });
  }
  
  return { count, base64Count, totalBase64Size };
};

// Helper to calculate data size
const calculateDataSize = (data: any): number => {
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch (e) {
    return 0;
  }
};

/**
 * Recursively finds and deletes any property that looks like a base64 image string.
 * This is an aggressive cleanup for legacy data that has embedded images.
 * @param obj The object to clean
 * @param parentKey The key path for logging purposes
 * @returns The number of base64 strings deleted and total bytes saved
 */
const recursivelyStripBase64Images = (obj: any, parentKey: string = 'root'): { count: number; bytesSaved: number } => {
  if (!obj || typeof obj !== 'object') {
    return { count: 0, bytesSaved: 0 };
  }

  let count = 0;
  let bytesSaved = 0;

  // Handle arrays
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const result = recursivelyStripBase64Images(item, `${parentKey}[${index}]`);
      count += result.count;
      bytesSaved += result.bytesSaved;
    });
    return { count, bytesSaved };
  }

  // Handle objects
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      // Check if this is a base64 image string
      if (typeof value === 'string' && value.startsWith('data:image/')) {
        const currentPath = `${parentKey}.${key}`;
        
        // IMPORTANT: Exclude RIR diagram image from cleanup - it's user data that needs to be saved
        if (currentPath.includes('roomInRoofData.diagramImage')) {
          console.log(`⚠️ [RECURSIVE CLEAN] Skipping RIR diagram image at ${currentPath} (user data, must preserve)`);
          continue; // Skip deletion for RIR diagram
        }
        
        const size = value.length;
        console.log(`🧹 [RECURSIVE CLEAN] Found base64 image at ${currentPath} (size: ${formatFileSize(size)})`);
        delete obj[key];
        count++;
        bytesSaved += size;
      } 
      // Recursively check nested objects
      else if (typeof value === 'object' && value !== null) {
        const result = recursivelyStripBase64Images(value, `${parentKey}.${key}`);
        count += result.count;
        bytesSaved += result.bytesSaved;
      }
    }
  }

  return { count, bytesSaved };
};

// Save only specific fields of a retrofit survey (partial update)
export const partialUpdateRetrofitSurvey = async (
  surveyId: string,
  updates: Partial<RetrofitSurveyData>,
  sectionName?: string
): Promise<void> => {
  try {
    // Create a deep copy and strip large dataUrl from elevations
    const dataToUpdate = JSON.parse(JSON.stringify({
      ...updates,
      updated_at: new Date().toISOString(),
    }));
    
    // Strip large dataUrl from all elevation types to prevent timeout
    if (dataToUpdate.measure_specification) {
      const elevationFields = ['wallInsulationElevations', 'rirExteriorElevations'];
      let totalStripped = 0;
      
      elevationFields.forEach(field => {
        if (Array.isArray(dataToUpdate.measure_specification[field])) {
          dataToUpdate.measure_specification[field].forEach((elevation: any) => {
            let itemStripped = false;
            
            if (elevation.dataUrl) {
              delete elevation.dataUrl;
              itemStripped = true;
            }
            
            if (elevation.base64String) {
              delete elevation.base64String;
              itemStripped = true;
            }
            
            if (itemStripped) totalStripped++;
          });
        }
      });
      
      if (totalStripped > 0) {
        console.log(`🧹 [PARTIAL UPDATE] Stripped ${totalStripped} elevation image(s)`);
      }
    }
    
    // Apply recursive cleanup to property elevation sections if they're being updated
    let recursiveTotalCount = 0;
    let recursiveTotalSaved = 0;
    
    const sectionsToClean = ['property_front', 'property_rear', 'property_left', 'property_right', 'room_details', 'measure_specification'];
    sectionsToClean.forEach(section => {
      if (dataToUpdate[section]) {
        const result = recursivelyStripBase64Images(dataToUpdate[section], section);
        recursiveTotalCount += result.count;
        recursiveTotalSaved += result.bytesSaved;
      }
    });
    
    if (recursiveTotalCount > 0) {
      console.log(`🧹 [PARTIAL UPDATE] Recursively removed ${recursiveTotalCount} embedded base64 image(s), saved ${formatFileSize(recursiveTotalSaved)}`);
    }

    // Calculate and log update size
    const jsonString = JSON.stringify(dataToUpdate);
    const sizeInBytes = new Blob([jsonString]).size;
    
    // 🔍 SMART PAYLOAD ANALYSIS: Check for base64 bloat vs legitimate large data
    const base64Pattern = /data:image\/[^;]+;base64,([^"]+)/g;
    const base64Matches = jsonString.match(base64Pattern) || [];
    const base64Count = base64Matches.length;
    const estimatedBase64Size = base64Matches.reduce((total, match) => {
      // Rough estimate: base64 string length * 0.75 = actual bytes
      return total + (match.length * 0.75);
    }, 0);
    
    const BASE64_THRESHOLD = 1 * 1024 * 1024; // 1 MB of base64 is too much
    const LARGE_PAYLOAD_WARNING = 10 * 1024 * 1024; // 10 MB warning threshold
    
    // 🚨 BLOCK: Base64 bloat detected (real problem)
    if (estimatedBase64Size > BASE64_THRESHOLD) {
      const error = new Error(`Base64 photo data detected: ${formatFileSize(estimatedBase64Size)} embedded images must be uploaded to CDN first`);
      console.error(`❌ [PARTIAL UPDATE] Base64 bloat detected:`, {
        id: surveyId,
        section: sectionName || 'multiple',
        totalSize: formatFileSize(sizeInBytes),
        base64Photos: base64Count,
        base64Size: formatFileSize(estimatedBase64Size),
        percentBase64: ((estimatedBase64Size / sizeInBytes) * 100).toFixed(1) + '%',
        recommendation: '⚠️ Photos must be uploaded to CDN before saving. Photo migration should run automatically.'
      });
      throw error;
    }
    
    // ⚠️ WARN: Large legitimate payload (CDN URLs, lots of data)
    if (sizeInBytes > LARGE_PAYLOAD_WARNING) {
      console.warn(`⚠️ [PARTIAL UPDATE] Large payload detected (this is OK if photos are on CDN):`, {
        id: surveyId,
        section: sectionName || 'multiple',
        sizeBytes: sizeInBytes,
        sizeFormatted: formatFileSize(sizeInBytes),
        base64Photos: base64Count,
        base64Size: formatFileSize(estimatedBase64Size),
        note: 'Large payloads with CDN URLs are fine, but may take longer to save'
      });
    } else {
      console.log(`📊 [PARTIAL UPDATE] Updating retrofit survey section:`, {
        id: surveyId,
        section: sectionName || 'multiple',
        sizeBytes: sizeInBytes,
        sizeFormatted: formatFileSize(sizeInBytes),
        base64Photos: base64Count,
        fields: Object.keys(updates),
        action: 'PARTIAL_UPDATE'
      });
    }

    const { error } = await supabase
      .from('retrofit_surveys')
      .update(dataToUpdate)
      .eq('id', surveyId);

    if (error) throw error;
    console.log(`[RetrofitSurveyStorage] ✅ Partial update successful for section: ${sectionName || 'multiple'}`);
  } catch (error) {
    console.error('[RetrofitSurveyStorage] Error in partial update:', error);
    throw error;
  }
};

// Save retrofit survey to Supabase
export const saveRetrofitSurveyToSupabase = async (
  name: string,
  data: RetrofitSurveyData,
  existingId?: string,
  forceInsert: boolean = false
): Promise<string> => {
  try {
    // Create a deep copy and strip large dataUrl from elevations
    const dataCopy = JSON.parse(JSON.stringify(data));
    
    // Strip large dataUrl from all elevation types to prevent timeout
    if (dataCopy.measure_specification) {
      const elevationFields = ['wallInsulationElevations', 'rirExteriorElevations'];
      let totalStripped = 0;
      
      elevationFields.forEach(field => {
        if (Array.isArray(dataCopy.measure_specification[field])) {
          dataCopy.measure_specification[field].forEach((elevation: any) => {
            let itemStripped = false;
            
            if (elevation.dataUrl) {
              delete elevation.dataUrl;
              itemStripped = true;
            }
            
            if (elevation.base64String) {
              delete elevation.base64String;
              itemStripped = true;
            }
            
            if (itemStripped) totalStripped++;
          });
        }
      });
      
      if (totalStripped > 0) {
        console.log(`🧹 [FULL SAVE] Stripped ${totalStripped} elevation image(s)`);
      }
    }
    
    // Apply recursive cleanup to catch any other embedded base64 images
    const recursiveResult1 = recursivelyStripBase64Images(dataCopy.room_details, 'room_details');
    const recursiveResult2 = recursivelyStripBase64Images(dataCopy.measure_specification, 'measure_specification');
    const recursiveResult3 = recursivelyStripBase64Images(dataCopy.property_front, 'property_front');
    const recursiveResult4 = recursivelyStripBase64Images(dataCopy.property_rear, 'property_rear');
    const recursiveResult5 = recursivelyStripBase64Images(dataCopy.property_left, 'property_left');
    const recursiveResult6 = recursivelyStripBase64Images(dataCopy.property_right, 'property_right');
    const totalRecursive = recursiveResult1.count + recursiveResult2.count + recursiveResult3.count + recursiveResult4.count + recursiveResult5.count + recursiveResult6.count;
    const totalRecursiveSaved = recursiveResult1.bytesSaved + recursiveResult2.bytesSaved + recursiveResult3.bytesSaved + recursiveResult4.bytesSaved + recursiveResult5.bytesSaved + recursiveResult6.bytesSaved;
    
    if (totalRecursive > 0) {
      console.log(`🧹 [FULL SAVE] Recursively removed ${totalRecursive} embedded base64 image(s), saved ${formatFileSize(totalRecursiveSaved)}`);
    }
    
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Get user's organization ID
    const { data: memberData } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', userId)
      .eq('is_active', true)
      .single();
    
    const organizationId = memberData?.organization_id || null;
    console.log(`[SaveRetrofitSurvey] 🏢 Organization ID: ${organizationId || 'none'}`);
    
    const dataToSave = {
      ...dataCopy,
      name,
      user_id: userId,
      organization_id: organizationId,
      updated_at: new Date().toISOString(),
      created_at: existingId ? data.created_at || new Date().toISOString() : new Date().toISOString(),
    };

    // Calculate and log file size
    const jsonString = JSON.stringify(dataToSave);
    const sizeInBytes = new Blob([jsonString]).size;
    const photoAnalysis = analyzePhotosInRetrofitSurvey(dataToSave);
    
    // 📊 SIZE BREAKDOWN: Analyze what contributes to total size
    const sizeBreakdown = analyzeSurveySize(dataToSave);
    console.log(`📊 [SIZE BREAKDOWN] Survey composition:`, {
      totalSize: formatFileSize(sizeBreakdown.totalSize),
      photoRelatedSize: formatFileSize(sizeBreakdown.photoRelatedSize),
      photoPercentage: ((sizeBreakdown.photoRelatedSize / sizeBreakdown.totalSize) * 100).toFixed(1) + '%',
      otherDataSize: formatFileSize(sizeBreakdown.otherDataSize),
      otherPercentage: ((sizeBreakdown.otherDataSize / sizeBreakdown.totalSize) * 100).toFixed(1) + '%',
      breakdown: {
        initial_photos: formatFileSize(sizeBreakdown.breakdown.initial_questionnaire_photos),
        heating_photos: formatFileSize(sizeBreakdown.breakdown.heating_system_photos),
        property_photos: formatFileSize(sizeBreakdown.breakdown.property_photos),
        room_photos: formatFileSize(sizeBreakdown.breakdown.room_photos),
        other_data: formatFileSize(sizeBreakdown.breakdown.other_data)
      }
    });
    
    const BASE64_THRESHOLD = 1 * 1024 * 1024; // 1 MB of base64 is too much
    const LARGE_PAYLOAD_WARNING = 10 * 1024 * 1024; // 10 MB warning threshold
    
    // 🚨 BLOCK: Base64 bloat detected (real problem)
    if (photoAnalysis.totalBase64Size > BASE64_THRESHOLD) {
      const error = new Error(`Base64 photo data detected: ${formatFileSize(photoAnalysis.totalBase64Size)} embedded images must be uploaded to CDN first`);
      console.error(`❌ [FULL SAVE] Base64 bloat detected:`, {
        id: existingId || 'new',
        name,
        totalSize: formatFileSize(sizeInBytes),
        photoCount: photoAnalysis.count,
        base64Photos: photoAnalysis.base64Count,
        base64Size: formatFileSize(photoAnalysis.totalBase64Size),
        percentBase64: ((photoAnalysis.totalBase64Size / sizeInBytes) * 100).toFixed(1) + '%',
        recommendation: '⚠️ Photos must be uploaded to CDN before saving. Photo migration should run automatically.'
      });
      throw error;
    }
    
    // ⚠️ WARN: Large legitimate payload (CDN URLs, lots of data)
    if (sizeInBytes > LARGE_PAYLOAD_WARNING) {
      console.warn(`⚠️ [FULL SAVE] Large survey detected (this is OK if photos are on CDN):`, {
        id: existingId || 'new',
        name,
        sizeBytes: sizeInBytes,
        sizeFormatted: formatFileSize(sizeInBytes),
        photoCount: photoAnalysis.count,
        base64Photos: photoAnalysis.base64Count,
        base64Size: formatFileSize(photoAnalysis.totalBase64Size),
        note: 'Large surveys with 400+ CDN photos are fine, but may take longer to save',
        action: existingId ? 'UPDATE' : 'INSERT'
      });
    } else {
      console.log(`📊 [FILE SIZE] Saving retrofit survey:`, {
        id: existingId || 'new',
        name,
        sizeBytes: sizeInBytes,
        sizeFormatted: formatFileSize(sizeInBytes),
        photoCount: photoAnalysis.count,
        base64Photos: photoAnalysis.base64Count,
        base64Size: formatFileSize(photoAnalysis.totalBase64Size),
        action: existingId ? 'UPDATE' : 'INSERT'
      });
    }
    
    // Warn if any base64 photos detected (but under threshold)
    if (photoAnalysis.base64Count > 0 && photoAnalysis.totalBase64Size <= BASE64_THRESHOLD) {
      console.warn(`⚠️ [FILE SIZE] WARNING: ${photoAnalysis.base64Count} photos still contain base64 data!`, {
        base64PhotoCount: photoAnalysis.base64Count,
        base64DataSize: formatFileSize(photoAnalysis.totalBase64Size),
        percentOfTotal: ((photoAnalysis.totalBase64Size / sizeInBytes) * 100).toFixed(1) + '%',
        status: 'Under threshold but should be migrated'
      });
    }

    // Helper function to handle missing custom_fields column
    const handleSaveError = async (error: any, data: any, isUpdate: boolean): Promise<string | null> => {
      if (error?.code === 'PGRST204' && error?.message?.includes('custom_fields')) {
        console.warn('⚠️ [RetrofitSurveyStorage] custom_fields column not found. Retrying without it...');
        console.warn('⚠️ Run migration: supabase/migrations/20260116_add_custom_fields_to_retrofit_surveys.sql');
        
        // Strip custom_fields and retry
        const { custom_fields, ...dataWithoutCustomFields } = data;
        
        if (isUpdate) {
          const { data: retryData, error: retryError } = await supabase
            .from('retrofit_surveys')
            .update(dataWithoutCustomFields)
            .eq('id', existingId!)
            .select()
            .single();
          
          if (retryError) throw retryError;
          console.log(`[RetrofitSurveyStorage] ✅ Saved without custom_fields (${existingId})`);
          return existingId!;
        } else {
          const insertData = existingId ? { ...dataWithoutCustomFields, id: existingId } : dataWithoutCustomFields;
          const { data: retryData, error: retryError } = await supabase
            .from('retrofit_surveys')
            .insert([insertData])
            .select()
            .single();
          
          if (retryError) throw retryError;
          console.log(`[RetrofitSurveyStorage] ✅ Created without custom_fields (${retryData.id})`);
          return retryData.id;
        }
      }
      return null; // Not a custom_fields error
    };

    if (existingId && !forceInsert) {
      // Try to update existing record
      const { data: updatedData, error } = await supabase
        .from('retrofit_surveys')
        .update(dataToSave)
        .eq('id', existingId)
        .select()
        .single();

      // Handle custom_fields column missing
      if (error) {
        const retryResult = await handleSaveError(error, dataToSave, true);
        if (retryResult) return retryResult;
      }

      // If record doesn't exist (PGRST116 error), fall back to insert
      if (error && error.code === 'PGRST116') {
        console.log(`[RetrofitSurveyStorage] Record ${existingId} not found, creating new record`);
        const { data: insertedData, error: insertError } = await supabase
          .from('retrofit_surveys')
          .insert([{ ...dataToSave, id: existingId }])
          .select()
          .single();

        if (insertError) {
          const retryResult = await handleSaveError(insertError, { ...dataToSave, id: existingId }, false);
          if (retryResult) return retryResult;
          throw insertError;
        }
        console.log(`[RetrofitSurveyStorage] Created new retrofit survey ${insertedData.id}`);
        return insertedData.id;
      }
      
      if (error) throw error;
      console.log(`[RetrofitSurveyStorage] Updated retrofit survey ${existingId}`);
      return existingId;
    } else {
      // Insert new record (with or without specific ID)
      const insertData = existingId ? { ...dataToSave, id: existingId } : dataToSave;
      const { data: insertedData, error } = await supabase
        .from('retrofit_surveys')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        const retryResult = await handleSaveError(error, insertData, false);
        if (retryResult) return retryResult;
        throw error;
      }
      console.log(`[RetrofitSurveyStorage] Created new retrofit survey ${insertedData.id}`);
      return insertedData.id;
    }
  } catch (error: any) {
    console.error('[RetrofitSurveyStorage] Error saving retrofit survey:', error);
    throw error;
  }
};

// Load retrofit survey from Supabase
export const loadRetrofitSurveyFromSupabase = async (id: string): Promise<RetrofitSurveyData> => {
  try {
    const { data, error } = await supabase
      .from('retrofit_surveys')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    if (!data) throw new Error(`Retrofit survey with ID ${id} not found`);
    
    // Clean up large dataUrl from old database records (backwards compatibility)
    if (data.measure_specification) {
      console.log('🔍 [CLEANUP] Checking measure_specification for elevation images...');
      const elevationFields = ['wallInsulationElevations', 'rirExteriorElevations'];
      let strippedCount = 0;
      
      elevationFields.forEach(field => {
        if (data.measure_specification[field]) {
          console.log(`🔍 [CLEANUP] Found field: ${field}, isArray: ${Array.isArray(data.measure_specification[field])}, length: ${data.measure_specification[field]?.length}`);
          
          if (Array.isArray(data.measure_specification[field])) {
            data.measure_specification[field].forEach((elevation: any, idx: number) => {
              let itemStripped = false;
              
              // Strip dataUrl (wall insulation elevations)
              if (elevation.dataUrl) {
                const dataUrlSize = elevation.dataUrl.length;
                console.log(`🧹 [CLEANUP] Stripping dataUrl from ${field}[${idx}] (size: ${(dataUrlSize / 1024 / 1024).toFixed(2)} MB)`);
                delete elevation.dataUrl;
                itemStripped = true;
              }
              
              // Strip base64String (RIR elevations)
              if (elevation.base64String) {
                const base64Size = elevation.base64String.length;
                console.log(`🧹 [CLEANUP] Stripping base64String from ${field}[${idx}] (size: ${(base64Size / 1024 / 1024).toFixed(2)} MB)`);
                delete elevation.base64String;
                itemStripped = true;
              }
              
              if (itemStripped) strippedCount++;
            });
          }
        }
      });
      
      if (strippedCount > 0) {
        console.log(`🧹 [LOAD] Stripped ${strippedCount} elevation dataUrl(s) from old database record`);
      } else {
        console.log('ℹ️ [CLEANUP] No elevation dataUrl found (already clean or no elevations)');
      }
    }
    
    // Apply recursive cleanup to large sections to find hidden base64 images
    console.log('🔍 [CLEANUP] Scanning for embedded base64 images in large sections...');
    let totalBase64Cleaned = 0;
    let totalBytesSaved = 0;
    
    // Clean room_details if it exists
    if (data.room_details) {
      const result = recursivelyStripBase64Images(data.room_details, 'room_details');
      totalBase64Cleaned += result.count;
      totalBytesSaved += result.bytesSaved;
    }
    
    // Clean measure_specification if it exists (beyond just elevations)
    if (data.measure_specification) {
      const result = recursivelyStripBase64Images(data.measure_specification, 'measure_specification');
      totalBase64Cleaned += result.count;
      totalBytesSaved += result.bytesSaved;
    }
    
    if (totalBase64Cleaned > 0) {
      console.log(`🧹 [RECURSIVE CLEAN] Removed ${totalBase64Cleaned} embedded base64 image(s), saved ${formatFileSize(totalBytesSaved)}`);
    } else {
      console.log('✅ [CLEANUP] No embedded base64 images found (data is clean!)');
    }

    // Calculate and log file size
    const jsonString = JSON.stringify(data);
    const sizeInBytes = new Blob([jsonString]).size;
    const photoAnalysis = analyzePhotosInRetrofitSurvey(data);
    
    // Calculate detailed size breakdown (always, not just for large files)
    const sizeBreakdown = {
      initial_questionnaire: calculateDataSize(data.initial_questionnaire),
      floor_plan: calculateDataSize(data.floor_plan),
      occupancy_assessment: calculateDataSize(data.occupancy_assessment),
      ventilation_condition: calculateDataSize(data.ventilation_condition),
      heating_system: calculateDataSize(data.heating_system),
      site_assessment: calculateDataSize(data.site_assessment),
      measure_specification: calculateDataSize(data.measure_specification),
      property_front: calculateDataSize(data.property_front),
      property_rear: calculateDataSize(data.property_rear),
      property_left: calculateDataSize(data.property_left),
      property_right: calculateDataSize(data.property_right),
      room_details: calculateDataSize(data.room_details),
      main_property_measurements: calculateDataSize(data.main_property_measurements),
      extensions: calculateDataSize(data.extensions)
    };
    
    // 📊 COMPREHENSIVE PHOTO COUNT: Use the existing photoAnalysis count
    const photoBreakdown = {
      initial_questionnaire: 0,
      heating_system: 0,
      property_front: 0,
      property_rear: 0,
      property_left: 0,
      property_right: 0,
      rooms: 0
    };
    
    // Count initial questionnaire photos
    if (data.initial_questionnaire?.photos) {
      Object.values(data.initial_questionnaire.photos).forEach((section: any) => {
        if (section?.photos && Array.isArray(section.photos)) {
          photoBreakdown.initial_questionnaire += section.photos.length;
        }
      });
    }
    
    // Count heating system photos
    if (data.heating_system?.photos) {
      Object.values(data.heating_system.photos).forEach((photoArray: any) => {
        if (Array.isArray(photoArray)) {
          photoBreakdown.heating_system += photoArray.length;
        }
      });
    }
    
    // Count property photos
    ['property_front', 'property_rear', 'property_left', 'property_right'].forEach(section => {
      const sectionData = (data as any)[section];
      if (sectionData) {
        Object.values(sectionData).forEach((value: any) => {
          if (Array.isArray(value) && value.length > 0) {
            // Check if it's a photo array
            const isPhotoArray = typeof value[0] === 'object' && (value[0].url || value[0].fileName || value[0].id);
            if (isPhotoArray) {
              const key = section as 'property_front' | 'property_rear' | 'property_left' | 'property_right';
              photoBreakdown[key] += value.length;
            }
          }
        });
      }
    });
    
    // Count room photos with detailed logging - DEEP SCAN
    const countPhotosInValue = (value: any): number => {
      let count = 0;
      
      if (Array.isArray(value)) {
        value.forEach((item: any) => {
          if (item && typeof item === 'object') {
            // Check if this item is a photo
            if (item.url || item.fileName || item.id || item.localPath || item.dataUrl || item.base64String) {
              count++;
            } else {
              // Recursively check nested objects
              count += countPhotosInValue(item);
            }
          }
        });
      } else if (value && typeof value === 'object') {
        // Check if this object itself is a photo
        if (value.url || value.fileName || value.id || value.localPath || value.dataUrl || value.base64String) {
          count++;
        } else {
          // Recursively check all properties of the object
          Object.values(value).forEach((nestedValue: any) => {
            count += countPhotosInValue(nestedValue);
          });
        }
      }
      
      return count;
    };
    
    if (data.room_details?.rooms && Array.isArray(data.room_details.rooms)) {
      const roomPhotoDebug: any[] = [];
      data.room_details.rooms.forEach((room: any, roomIndex: number) => {
        let roomPhotoCount = 0;
        const roomPhotoFields: string[] = [];
        
        Object.keys(room).forEach((key: string) => {
          const value = room[key];
          const photosInField = countPhotosInValue(value);
          
          if (photosInField > 0) {
            photoBreakdown.rooms += photosInField;
            roomPhotoCount += photosInField;
            roomPhotoFields.push(`${key}:${photosInField}`);
          }
        });
        
        if (roomPhotoCount > 0) {
          roomPhotoDebug.push({
            roomIndex,
            roomName: room.roomName || room.name || `Room ${roomIndex}`,
            photoCount: roomPhotoCount,
            fields: roomPhotoFields
          });
        }
      });
      
      // Log room photo details
      if (roomPhotoDebug.length > 0) {
        console.log(`📊 [ROOM PHOTO DETAILS] Deep scan found photos in ${roomPhotoDebug.length} rooms:`, roomPhotoDebug);
      }
    }
    
    const totalPhotosDetailed = Object.values(photoBreakdown).reduce((sum, count) => sum + count, 0);
    
    // Single consolidated log with all info
    console.log(`
📊 ========================================
📊 [SURVEY LOADED] ${data.name || 'Unnamed Survey'}
📊 ========================================
📊 Total File Size: ${formatFileSize(sizeInBytes)} (${sizeInBytes.toLocaleString()} bytes)
📊 Total Photos: ${photoAnalysis.count} photos (${totalPhotosDetailed} detected in breakdown)
📊 Photo Breakdown:
   • Initial Questionnaire: ${photoBreakdown.initial_questionnaire} photos
   • Heating System: ${photoBreakdown.heating_system} photos
   • Property Front: ${photoBreakdown.property_front} photos
   • Property Rear: ${photoBreakdown.property_rear} photos
   • Property Left: ${photoBreakdown.property_left} photos
   • Property Right: ${photoBreakdown.property_right} photos
   • Room Photos: ${photoBreakdown.rooms} photos
📊 Base64 Photos: ${photoAnalysis.base64Count} (${formatFileSize(photoAnalysis.totalBase64Size)})
📊 Created: ${new Date(data.created_at).toLocaleString()}
📊 Last Updated: ${new Date(data.updated_at).toLocaleString()}
📊 ========================================
    `.trim());
    
    // ALWAYS show comprehensive size breakdown
    console.group('📊 [FILE SIZE] Detailed Size Breakdown');
    
    // Sort all sections by size (largest first)
    const sortedSections = Object.entries(sizeBreakdown)
      .sort((a, b) => b[1] - a[1])
      .filter(([_, bytes]) => bytes > 0);
    
    sortedSections.forEach(([section, bytes]) => {
      const sizeStr = formatFileSize(bytes);
      const percentage = ((bytes / sizeInBytes) * 100).toFixed(1);
      const emoji = bytes > 1024 * 1024 ? '🔴' : bytes > 100 * 1024 ? '🟡' : '🟢';
      console.log(`${emoji} ${section}: ${sizeStr} (${percentage}%)`);
    });
    
    console.groupEnd();
    
    // Highlight sections over 1MB
    const largeSections = Object.entries(sizeBreakdown)
      .filter(([_, bytes]) => bytes > 1024 * 1024)
      .sort((a, b) => b[1] - a[1]);
    
    if (largeSections.length > 0) {
      console.warn(`🔍 [FILE SIZE] Sections over 1MB:`, largeSections.map(([section, bytes]) => 
        `${section}: ${formatFileSize(bytes)}`
      ).join(', '));
      
      // Detailed breakdown for measure_specification if it's large
      if (data.measure_specification && sizeBreakdown.measure_specification > 1024 * 1024) {
        const msBreakdown: Record<string, number> = {};
        Object.keys(data.measure_specification).forEach(key => {
          msBreakdown[key] = calculateDataSize(data.measure_specification[key]);
        });
        
        console.warn(`🔍 [MEASURE SPEC] Detailed breakdown:`, Object.entries(msBreakdown)
          .filter(([_, bytes]) => bytes > 10000) // Only show items > 10KB
          .sort((a, b) => b[1] - a[1])
          .reduce((acc, [key, bytes]) => {
            acc[key] = formatFileSize(bytes);
            return acc;
          }, {} as Record<string, string>));
        
        // Log structure of large fields
        Object.entries(msBreakdown)
          .filter(([_, bytes]) => bytes > 100000) // Fields > 100KB
          .sort((a, b) => b[1] - a[1])
          .forEach(([key, bytes]) => {
            const field = data.measure_specification[key];
            console.warn(`🔍 [LARGE FIELD] ${key} (${formatFileSize(bytes)}):`, {
              type: Array.isArray(field) ? 'array' : typeof field,
              length: Array.isArray(field) ? field.length : undefined,
              keys: typeof field === 'object' && field !== null ? Object.keys(field).slice(0, 10) : undefined,
              sampleItem: Array.isArray(field) && field.length > 0 ? {
                hasDataUrl: !!field[0]?.dataUrl,
                dataUrlSize: field[0]?.dataUrl ? `${(field[0].dataUrl.length / 1024 / 1024).toFixed(2)} MB` : 'N/A',
                otherKeys: Object.keys(field[0] || {})
              } : undefined
            });
          });
      }
      
      // Detailed breakdown for initial_questionnaire if it's large
      if (data.initial_questionnaire && sizeBreakdown.initial_questionnaire > 1024 * 1024) {
        const iqBreakdown: Record<string, number> = {};
        Object.keys(data.initial_questionnaire).forEach(key => {
          iqBreakdown[key] = calculateDataSize((data.initial_questionnaire as any)[key]);
        });
        
        console.warn(`🔍 [INITIAL QUESTIONNAIRE] Detailed breakdown:`, Object.entries(iqBreakdown)
          .filter(([_, bytes]) => bytes > 10000) // Only show items > 10KB
          .sort((a, b) => b[1] - a[1])
          .reduce((acc, [key, bytes]) => {
            acc[key] = formatFileSize(bytes);
            return acc;
          }, {} as Record<string, string>));
        
        // Log structure of large fields
        Object.entries(iqBreakdown)
          .filter(([_, bytes]) => bytes > 100000) // Fields > 100KB
          .sort((a, b) => b[1] - a[1])
          .forEach(([key, bytes]) => {
            const field = (data.initial_questionnaire as any)[key];
            console.warn(`🔍 [LARGE FIELD] initial_questionnaire.${key} (${formatFileSize(bytes)}):`, {
              type: Array.isArray(field) ? 'array' : typeof field,
              length: Array.isArray(field) ? field.length : undefined,
              keys: typeof field === 'object' && field !== null ? Object.keys(field).slice(0, 20) : undefined,
              sampleValue: typeof field === 'string' ? field.substring(0, 200) + '...' : undefined,
              sampleItem: Array.isArray(field) && field.length > 0 ? {
                type: typeof field[0],
                hasDataUrl: !!field[0]?.dataUrl,
                dataUrlSize: field[0]?.dataUrl ? `${(field[0].dataUrl.length / 1024 / 1024).toFixed(2)} MB` : 'N/A',
                isString: typeof field[0] === 'string',
                stringPreview: typeof field[0] === 'string' ? field[0].substring(0, 100) : undefined,
                otherKeys: typeof field[0] === 'object' ? Object.keys(field[0] || {}) : undefined
              } : undefined
            });
          });
      }
    }
    
    // Warn if base64 photos detected
    if (photoAnalysis.base64Count > 0) {
      console.warn(`⚠️ [FILE SIZE] WARNING: ${photoAnalysis.base64Count} photos still contain base64 data!`, {
        base64PhotoCount: photoAnalysis.base64Count,
        base64DataSize: formatFileSize(photoAnalysis.totalBase64Size),
        percentOfTotal: ((photoAnalysis.totalBase64Size / sizeInBytes) * 100).toFixed(1) + '%'
      });
    }
    
    // Warn if file is large
    if (sizeInBytes > 5 * 1024 * 1024) {
      console.warn(`⚠️ [FILE SIZE] Large file detected: ${formatFileSize(sizeInBytes)}`);
    }
    
    // 💾 CALCULATE ACTUAL STORAGE SIZE: Query Supabase Storage for photo file sizes
    console.log(`💾 [STORAGE SIZE] Calculating actual photo storage size in Supabase...`);
    const storageAnalysis = await calculateStorageSize(data);
    
    console.log(`
💾 ========================================
💾 [SUPABASE STORAGE] Actual Photo Storage
💾 ========================================
💾 Total Photo Storage: ${formatFileSize(storageAnalysis.totalBytes)}
💾 Photo Files Analyzed: ${storageAnalysis.breakdown.successCount} / ${storageAnalysis.photoCount}
💾 Failed to Analyze: ${storageAnalysis.breakdown.errorCount}
💾 
💾 TOTAL SUPABASE USAGE FOR THIS SURVEY:
💾 • Database Row (JSON): ${formatFileSize(sizeInBytes)}
💾 • Storage (Photos): ${formatFileSize(storageAnalysis.totalBytes)}
💾 • Combined Total: ${formatFileSize(sizeInBytes + storageAnalysis.totalBytes)}
💾 ========================================
    `.trim());

    return data as RetrofitSurveyData;
  } catch (error) {
    console.error('[RetrofitSurveyStorage] Error loading retrofit survey:', error);
    throw error;
  }
};

// Get property address from retrofit survey data
export const getRetrofitSurveyAddress = (data: RetrofitSurveyData): string => {
  const { houseNumberName, streetAddress, town, postCode } = data.initial_questionnaire;
  
  // Combine house number/name with street address if present
  const fullStreetAddress = houseNumberName && houseNumberName.trim()
    ? `${houseNumberName.trim()} ${streetAddress}`
    : streetAddress;
  
  const parts = [fullStreetAddress, town, postCode].filter(part => part && part.trim());
  return parts.join(', ');
};

// Validate retrofit survey data before saving
export const validateRetrofitSurveyData = (data: RetrofitSurveyData, context?: { address?: string }): { isValid: boolean; message?: string } => {
  const addressToValidate = context?.address !== undefined 
    ? context.address 
    : getRetrofitSurveyAddress(data);
    
  if (!addressToValidate || addressToValidate.trim().length < 3) {
    return { isValid: false, message: 'Property address must be at least 3 characters long' };
  }
  
  return { isValid: true };
};

// Log room sync operation for debugging
export const logRoomSyncOperation = (
  operation: string,
  floorPlanRoomsCount: number,
  existingRoomsCount: number,
  resultingRoomsCount: number
): void => {
  console.log(`[RetrofitSurveyStorage] ${operation}:`, {
    floorPlanRooms: floorPlanRoomsCount,
    existingRooms: existingRoomsCount,
    resultingRooms: resultingRoomsCount,
    timestamp: new Date().toISOString()
  });
};

// Generate filename for retrofit survey
export const generateRetrofitSurveyFilename = (data: RetrofitSurveyData): string => {
  const address = getRetrofitSurveyAddress(data);
  if (!address || address.trim().length < 3) {
    return `survey-${Date.now()}`;
  }
  return address.trim().replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_').substring(0, 50);
};

// Helper to get existing room by stable ID
export const findExistingRoom = (
  rooms: RetrofitSurveyData['room_details']['rooms'],
  stableId: string
): RetrofitSurveyData['room_details']['rooms'][0] | undefined => {
  return rooms.find(room => room.id === stableId);
};

// Merge existing room data with new floor plan data
export const mergeRoomData = (
  existingRoom: RetrofitSurveyData['room_details']['rooms'][0] | undefined,
  newRoomData: RetrofitSurveyData['room_details']['rooms'][0]
): RetrofitSurveyData['room_details']['rooms'][0] => {
  if (!existingRoom) {
    return newRoomData;
  }
  
  // Preserve user-entered data, update floor plan dimensions
  return {
    ...existingRoom,
    name: newRoomData.name, // Update name to reflect any label changes
    abbreviation: newRoomData.abbreviation,
    width: newRoomData.width, // Always update dimensions from floor plan
    length: newRoomData.length,
    area: newRoomData.area,
    height: newRoomData.height || existingRoom.height // Preserve existing height if no new height
  };
};

// Delete retrofit survey from Supabase
export const deleteRetrofitSurveyFromSupabase = async (id: string): Promise<void> => {
  try {
    // First, get the survey data to extract the property address
    console.log(`[RetrofitSurveyStorage] 🗑️ Fetching survey ${id} for deletion...`);
    const { data: survey, error: fetchError } = await supabase
      .from('retrofit_surveys')
      .select('name, initial_questionnaire')
      .eq('id', id)
      .single();
    
    if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = not found, which is OK
      console.warn('[RetrofitSurveyStorage] ⚠️ Error fetching survey for deletion:', fetchError);
    }
    
    // Try to delete photos if we have the initial_questionnaire data
    if (survey?.initial_questionnaire) {
      try {
        // Build minimal RetrofitSurveyData object for utility function
        const minimalSurveyData = {
          initial_questionnaire: survey.initial_questionnaire
        } as any;
        
        // Extract property address from survey data
        const propertyAddress = getRetrofitSurveyAddress(minimalSurveyData);
        
        if (propertyAddress) {
          console.log(`[RetrofitSurveyStorage] 📸 Deleting photos for property: ${propertyAddress}`);
          const { deleteAllSurveyPhotos } = await import('./supabaseStorage');
          await deleteAllSurveyPhotos(propertyAddress);
          console.log(`[RetrofitSurveyStorage] ✅ Photos deleted successfully`);
        } else {
          console.log(`[RetrofitSurveyStorage] ℹ️ No property address found, skipping photo deletion`);
        }
      } catch (photoError) {
        console.warn('[RetrofitSurveyStorage] ⚠️ Failed to delete photos (continuing with survey deletion):', photoError);
        // Don't fail the whole operation if photo deletion fails
      }
    } else {
      console.log(`[RetrofitSurveyStorage] ℹ️ No survey data found, skipping photo deletion`);
    }
    
    // Delete the survey record from database
    console.log(`[RetrofitSurveyStorage] 🗑️ Deleting survey record ${id}...`);
    const { error } = await supabase
      .from('retrofit_surveys')
      .delete()
      .eq('id', id);

    if (error) throw error;
    
    console.log(`[RetrofitSurveyStorage] ✅ Deleted retrofit survey ${id} and all associated photos`);
  } catch (error) {
    console.error('[RetrofitSurveyStorage] ❌ Error deleting retrofit survey:', error);
    throw error;
  }
};

// Helper function to extract abbreviation from room name
const extractRoomAbbreviation = (roomName: string): string => {
  // Look for pattern like "Bedroom 1 (BR1)" or "Kitchen (K)"
  const match = roomName.match(/\((\w+)\)$/);
  return match ? match[1] : '';
};

// Helper function to calculate room dimensions from floor plan room data
const calculateRoomDimensions = (room: Room): { width: number; length: number; area: number } => {
  try {
    const bounds = getRoomBounds(room.walls);
    const area = calculateArea(room.walls);
    
    // Calculate width and length from bounds (in meters)
    const width = Math.abs(bounds.maxX - bounds.minX) / 1000; // Convert from pixels to meters
    const length = Math.abs(bounds.maxY - bounds.minY) / 1000; // Convert from pixels to meters
    
    return {
      width: Math.round(width * 100) / 100, // Round to 2 decimal places
      length: Math.round(length * 100) / 100, // Round to 2 decimal places
      area: Math.round(area * 100) / 100 // Round to 2 decimal places
    };
  } catch (error) {
    console.warn('[RetrofitSurveyStorage] Error calculating room dimensions:', error);
    return { width: 0, length: 0, area: 0 };
  }
};

// Sync floor plan rooms to retrofit survey room details
export const syncFloorPlanRoomsToRetrofitSurvey = (
  floorPlanRooms: Room[],
  customLabels: Record<string, string>,
  siteNotesData: any,
  currentFloor: number
): RetrofitSurveyData['room_details']['rooms'] => {
  return floorPlanRooms.map(room => {
    // Ensure room has a stable ID
    if (!room.stableId) {
      console.warn('[RetrofitSurveyStorage] Room missing stableId, skipping:', room.name);
      return null;
    }
    
    // Get custom label if available
    const customLabel = customLabels[room.stableId] || room.name;
    const abbreviation = extractRoomAbbreviation(customLabel);
    
    // Calculate dimensions
    const dimensions = calculateRoomDimensions(room);
    
    // Get height from site notes data if available
    let height: number | undefined;
    if (siteNotesData?.floors) {
      const floorData = siteNotesData.floors.find((f: any) => f.id === currentFloor);
      if (floorData?.height) {
        const heightStr = floorData.height.toString().replace(/[^0-9.]/g, '');
        const heightNum = parseFloat(heightStr);
        if (!isNaN(heightNum) && heightNum > 0) {
          height = Math.round(heightNum * 100) / 100;
        }
      }
    }
    
    // Create room entry with proper name formatting
    const fullName = abbreviation 
      ? `${room.name} (${abbreviation})`
      : customLabel;
    
    return {
      id: room.stableId, // We've already checked this is defined above
      name: fullName,
      abbreviation,
      floor: currentFloor,
      usage: '', // To be filled by user
      heatingType: '', // To be filled by user
      ventilationType: '', // To be filled by user
      condition: '', // To be filled by user
      notes: '',
      photos: [],
      width: dimensions.width,
      length: dimensions.length,
      height,
      area: dimensions.area
    };
  }).filter(Boolean) as RetrofitSurveyData['room_details']['rooms']; // Filter out null values
};

// Update retrofit survey with room data
export const updateRetrofitSurveyRooms = (
  surveyData: RetrofitSurveyData,
  rooms: RetrofitSurveyData['room_details']['rooms']
): RetrofitSurveyData => {
  return {
    ...surveyData,
    room_details: {
      ...surveyData.room_details,
      rooms
    },
    updated_at: new Date().toISOString()
  };
};
